/**
 * Organization Users Management Page
 *
 * Reason: Allows admins to view and manage users in their organization,
 * including inviting new users, changing roles, and activating/deactivating users
 */

import { FC, useState, useContext, useEffect } from 'react';
import { Box, Typography, Chip } from '@mui/material';
import MuiAccordion, { AccordionProps } from '@mui/material/Accordion';
import MuiAccordionSummary, { AccordionSummaryProps } from '@mui/material/AccordionSummary';
import MuiAccordionDetails from '@mui/material/AccordionDetails';
import { styled } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import { useDebouncedCallback } from 'use-debounce';

import { UserContext } from '@/contexts/UserContext';
import { useListOrgUsers, OrgUser } from '@/adapters/users-v2';
import useAuthorization from '@/hooks/useAuthorization';
import PrimaryButton from '@/components/common/PrimaryButton';
import DefaultButton from '@/components/common/DefaultButton';
import Loading from '@/components/Loading';

import styles from './OrgUsers.module.scss';
import InviteUserDialog from './InviteUserDialog';
import EditUserDialog from './EditUserDialog';
import UploadSpeakersDialog from './UploadSpeakersDialog';

const Accordion = styled((props: AccordionProps) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
  border: `1px solid var(--silver)`,
  borderRadius: '8px',
  marginBottom: '15px',
  '&:before': {
    display: 'none',
  },
}));

const AccordionSummary = styled((props: AccordionSummaryProps) => (
  <MuiAccordionSummary {...props} />
))(({ theme }) => ({
  backgroundColor: 'var(--gallery)',
  borderBottomColor: 'transparent',
  borderRadius: '8px',
  flexDirection: 'row-reverse',
  padding: 0,
  '&.Mui-expanded': {
    borderRadius: '8px 8px 0 0',
  },
  '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
    transform: 'rotate(90deg)',
  },
  '& .MuiAccordionSummary-content': {
    margin: 0,
    padding: '10px',
  },
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: '16px',
  borderTop: '1px solid var(--silver)',
}));

const roleColorMap: Record<string, 'primary' | 'secondary' | 'default'> = {
  admin: 'primary',
  manager: 'secondary',
  viewer: 'default',
};

const statusColorMap: Record<string, 'success' | 'warning' | 'error' | 'default'> = {
  accepted: 'success',
  invited: 'warning',
  declined: 'error',
  expired: 'default',
};

const getStatusLabel = (status: string): string => {
  return status.charAt(0).toUpperCase() + status.slice(1);
};

const OrgUsers: FC = () => {
  const [searchValue, setSearchValue] = useState<string>('');
  const [filteredUsers, setFilteredUsers] = useState<OrgUser[]>([]);
  const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);
  const [isUploadSpeakersDialogOpen, setIsUploadSpeakersDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<OrgUser | null>(null);

  const { userState } = useContext(UserContext);
  const { hasRole } = useAuthorization();

  const { data: users = [], isLoading, refetch } = useListOrgUsers(userState?.orgId);

  // Check if user is admin
  const isAdmin = hasRole(['admin', 'super_admin']);

  /**
   * Filters users based on search query
   *
   * @reason Enables searching users by email or name for easier management
   */
  const debouncedSearch = useDebouncedCallback((value: string) => {
    if (!value.trim()) {
      setFilteredUsers(users);
      return;
    }

    const searchLower = value.toLowerCase();
    const filtered = users.filter(
      (user) =>
        user.email.toLowerCase().includes(searchLower) ||
        user.name?.toLowerCase().includes(searchLower),
    );
    setFilteredUsers(filtered);
  }, 300);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const value = event.target.value;
    setSearchValue(value);
    debouncedSearch(value);
  };

  const handleInviteUser = (): void => {
    setIsInviteDialogOpen(true);
  };

  const handleUploadSpeakers = (): void => {
    setIsUploadSpeakersDialogOpen(true);
  };

  const handleEditUser = (user: OrgUser): void => {
    setEditingUser(user);
  };

  const handleDialogClose = (): void => {
    setIsInviteDialogOpen(false);
    setIsUploadSpeakersDialogOpen(false);
    setEditingUser(null);
    void refetch();
  };

  // Update filtered users when users data changes
  useEffect(() => {
    setFilteredUsers(users);
  }, [users]);

  // If not admin, show access denied
  if (!isAdmin) {
    return (
      <div className={styles.container}>
        <Typography variant="h5" color="error">
          Access Denied
        </Typography>
        <Typography>Only admins can manage users.</Typography>
      </div>
    );
  }

  const displayUsers = searchValue ? filteredUsers : users;

  return (
    <>
      <div className={styles.navbar}>
        <h1 className={styles.projectsHeadTitle}>Organization Users</h1>
        <div className={styles.searchBar}>
          <input
            type="text"
            value={searchValue}
            onChange={handleSearchChange}
            placeholder="Search by email or name"
          />
          <SearchIcon sx={{ color: '#7a7a7a', position: 'absolute', right: '16px', top: '7px' }} />
        </div>
        <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
          <PrimaryButton onClick={handleInviteUser}>
            <div className={styles.buttonLabel} style={{ display: 'flex', alignItems: 'center' }}>
              Invite User
            </div>
          </PrimaryButton>
          {isAdmin && (
            <PrimaryButton onClick={handleUploadSpeakers}>
              <div className={styles.buttonLabel} style={{ display: 'flex', alignItems: 'center' }}>
                Upload Users Sheet
              </div>
            </PrimaryButton>
          )}
        </Box>
      </div>

      {isLoading ? (
        <Loading />
      ) : displayUsers.length === 0 ? (
        <Box sx={{ textAlign: 'center', marginTop: 4 }}>
          <Typography variant="h6">
            {searchValue ? 'No users found' : 'No users in organization'}
          </Typography>
        </Box>
      ) : (
        <div className={styles.table}>
          <div className={styles.tableTitle}>
            <div className={styles.tableItem}>Email</div>
            <div className={styles.tableItem}>Name</div>
            <div className={styles.tableItem}>Role</div>
            <div className={styles.tableItem}>Status</div>
            <div className={styles.tableItem}>Actions</div>
          </div>

          {displayUsers.map((user) => (
            <Accordion key={user.id}>
              <AccordionSummary>
                <Typography
                  sx={{ flex: '2 1', padding: 0 }}
                  className={styles.tableItem + ' ' + styles.specialColumn}
                >
                  {user.email}
                </Typography>
                <Typography sx={{ flex: '1 1', padding: 0 }} className={styles.specialColumn}>
                  {user.name || '-'}
                </Typography>
                <Box sx={{ flex: '1 1', padding: 0 }} className={styles.specialColumn}>
                  <Chip
                    label={user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                    color={roleColorMap[user.role]}
                    size="small"
                  />
                </Box>
                <Box sx={{ flex: '1 1', padding: 0 }} className={styles.specialColumn}>
                  <Chip
                    label={getStatusLabel(user.status)}
                    color={statusColorMap[user.status] || 'default'}
                    size="small"
                    variant="outlined"
                  />
                </Box>
                <Box sx={{ flex: '1 2', padding: 0, display: 'flex' }} className={styles.tableBtn}>
                  <DefaultButton
                    size="small"
                    sx={{ paddingLeft: 1, paddingRight: 1 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditUser(user);
                    }}
                  >
                    Edit
                  </DefaultButton>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Box>
                  <Typography variant="body2" color="textSecondary">
                    User ID: {user.id}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Created: {new Date(user.createdAt).toLocaleDateString()}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Last Updated: {new Date(user.updatedAt).toLocaleDateString()}
                  </Typography>
                </Box>
              </AccordionDetails>
            </Accordion>
          ))}
        </div>
      )}

      <InviteUserDialog open={isInviteDialogOpen} onClose={handleDialogClose} />
      <UploadSpeakersDialog open={isUploadSpeakersDialogOpen} onClose={handleDialogClose} />
      <EditUserDialog open={!!editingUser} user={editingUser} onClose={handleDialogClose} />
    </>
  );
};

export default OrgUsers;
